/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import { createContext, useContext, useMemo, type ReactNode } from "react";

export type UiOptionsValue = {
  /** Options for enabling and disabling experimental or testing features */
  experimental?: {
    /**
     * If true, the date field segments has leading zeros
     */
    enableShouldForceLeadingZerosInDateField?: boolean;

    /**
     * If enabled, the component Alert with severity equal to info will have black background color.
     * The option is experimental until the task is completed. https://st.yandex-team.ru/SUPERWEB-1365
     */
    alertInfoBlackBackground?: boolean;

    /**
     * Enable new scroll behavior for ListDetails component.
     */
    enableNewScrollBehaviorForListDetails?: boolean;

    /**
     * Enable delay show calendar dialog in mobile
     */
    enableDelayShowCalendarDialogInMobile?: boolean;

    /**
     * enable TagEditButton has no width limit
     */
    enableTagEditButtonHasNoWidthLimit?: boolean;

    /**
     * If enabled, TagField input will be hidden when not focused,
     * and visible when focused. This prevents empty line below tags.
     */
    tagFieldHideInputWhenNofocus?: boolean;

    /**
     * If true, the decorative elements for the new year are displayed.
     */
    enableNewYearView?: boolean;

    /**
     * If enabled, tags will wrap around the clear button, and the Chevron button will be hidden.
     * The input is displayed on the same line as the tags. If there is not enough horizontal
     * space, the input will wrap to the next line. This enables a more compact layout of the component.
     */
    tagFieldCompact?: boolean;

    /**
     * If enabled, the NumberField currency, unit, or percent is displayed in the label instead of the input.
     */
    numberFieldSeparateSymbol?: boolean;

    /**
     * If enabled, the NumberField applies formatting only on blur.
     */
    numberFieldFormatOnBlur?: boolean;
  };
};

const defaultValue: UiOptionsValue = {
  experimental: {
    enableDelayShowCalendarDialogInMobile: true,
    enableNewScrollBehaviorForListDetails: true,
  },
};

const context = createContext<UiOptionsValue>(defaultValue);

/**
 * Allows to set options for all components from `@superweb/ui`
 */
export const UiOptions = ({
  value,
  children,
}: {
  value: UiOptionsValue;
  children: ReactNode;
}) => {
  const nextValue = useMemo(() => {
    return {
      ...defaultValue,
      ...value,
      experimental: {
        ...defaultValue.experimental,
        ...value.experimental,
      },
    };
  }, [value]);

  return <context.Provider value={nextValue}>{children}</context.Provider>;
};
/**
 * @internal
 * Hook for get options
 */
export const useUiOptions = () => {
  return useContext(context);
};
